import { debug, toHex } from './util';
import { flags, sizes } from './cpu6502';
const alwaysBreak = (_info) => {
    return true;
};
/**
 * Converts a status register value into the well known string version
 * of the status register.
 *
 * @param sr Status register value
 * @returns Status register value as a string
 */
export const dumpStatusRegister = (sr) => [
    sr & flags.N ? 'N' : '-',
    sr & flags.V ? 'V' : '-',
    sr & flags.X ? 'X' : '-',
    sr & flags.B ? 'B' : '-',
    sr & flags.D ? 'D' : '-',
    sr & flags.I ? 'I' : '-',
    sr & flags.Z ? 'Z' : '-',
    sr & flags.C ? 'C' : '-',
].join('');
/**
 * Class that can be used to wrap the CPU6502 object to provide
 * additional support for stepping, tracing and setting breakpoints.
 */
export class Debugger {
    constructor(cpu, container) {
        this.cpu = cpu;
        this.container = container;
        this.verbose = false;
        this.maxTrace = 256;
        this.trace = [];
        this.breakpoints = new Map();
        this.symbols = {};
        this.break = () => {
            this.container.stop();
        };
        this.step = () => {
            this.cpu.step(() => {
                const info = this.cpu.getDebugInfo();
                debug(this.printDebugInfo(info));
                this.updateTrace(info);
            });
        };
        this.continue = () => {
            this.container.run();
        };
        /**
         * Restart at a given memory address.
         *
         * @param address Address to start execution
         */
        this.runAt = (address) => {
            this.cpu.reset();
            this.cpu.setPC(address);
        };
        this.isRunning = () => this.container.isRunning();
        this.setVerbose = (verbose) => {
            this.verbose = verbose;
        };
        this.setMaxTrace = (maxTrace) => {
            this.maxTrace = maxTrace;
        };
        this.getTrace = (count) => {
            return this.trace
                .slice(count ? -count : undefined)
                .map(this.printDebugInfo)
                .join('\n');
        };
        this.printTrace = (count) => {
            debug(this.getTrace(count));
        };
        this.getStack = (size) => {
            const { sp } = this.cpu.getDebugInfo();
            const stack = [];
            let max = 255;
            let min = 0;
            if (size) {
                if (sp - 3 >= 255 - size) {
                    min = Math.max(255 - size + 1, 0);
                }
                else {
                    max = Math.min(sp + size - 4, 255);
                    min = Math.max(sp - 3, 0);
                }
            }
            for (let addr = max; addr >= min; addr--) {
                const isSP = addr === sp ? '*' : ' ';
                const addrStr = `$${toHex(0x0100 + addr)}`;
                const valStr = toHex(this.cpu.read(0x01, addr));
                if (!size || (sp + size > addr && addr > sp - size)) {
                    stack.push(`${isSP} ${addrStr} ${valStr}`);
                }
            }
            return stack.join('\n');
        };
        this.setBreakpoint = (addr, exp) => {
            this.breakpoints.set(addr, exp || alwaysBreak);
        };
        this.clearBreakpoint = (addr) => {
            this.breakpoints.delete(addr);
        };
        this.listBreakpoints = () => {
            for (const [addr, fn] of this.breakpoints.entries()) {
                debug(toHex(addr, 4), fn);
            }
        };
        this.addSymbols = (symbols) => {
            this.symbols = Object.assign(Object.assign({}, this.symbols), symbols);
        };
        this.printDebugInfo = (info) => {
            const { pc, cmd } = info;
            const symbol = this.padWithSymbol(pc);
            return [
                toHex(pc, 4),
                '- ',
                symbol,
                this.dumpRegisters(info),
                ' ',
                this.dumpRawOp(cmd),
                ' ',
                this.dumpOp(pc, cmd),
            ].join('');
        };
        this.dumpPC = (pc) => {
            const b = this.cpu.read(pc);
            const op = this.cpu.getOpInfo(b);
            const size = sizes[op.mode];
            let result = toHex(pc, 4) + '- ';
            result += this.padWithSymbol(pc);
            const cmd = new Array(size);
            for (let idx = 0, jdx = pc; idx < size; idx++, jdx++) {
                cmd[idx] = this.cpu.read(jdx);
            }
            result += this.dumpRawOp(cmd) + ' ' + this.dumpOp(pc, cmd);
            return result;
        };
        this.dumpRegisters = (debugInfo) => {
            if (debugInfo === undefined) {
                debugInfo = this.cpu.getDebugInfo();
            }
            const { ar, xr, yr, sr, sp } = debugInfo;
            return [
                'A=' + toHex(ar),
                ' X=' + toHex(xr),
                ' Y=' + toHex(yr),
                ' P=' + toHex(sr),
                ' S=' + toHex(sp),
                ' ',
                dumpStatusRegister(sr),
            ].join('');
        };
        this.dumpPage = (start, end) => {
            let result = '';
            if (end === undefined) {
                end = start;
            }
            for (let page = start; page <= end; page++) {
                for (let idx = 0; idx < 16; idx++) {
                    result += toHex(page) + toHex(idx << 4) + ': ';
                    for (let jdx = 0; jdx < 16; jdx++) {
                        const b = this.cpu.read(page, idx * 16 + jdx);
                        result += toHex(b) + ' ';
                    }
                    result += '        ';
                    for (let jdx = 0; jdx < 16; jdx++) {
                        const b = this.cpu.read(page, idx * 16 + jdx) & 0x7f;
                        if (b >= 0x20 && b < 0x7f) {
                            result += String.fromCharCode(b);
                        }
                        else {
                            result += '.';
                        }
                    }
                    result += '\n';
                }
            }
            return result;
        };
        this.list = (pc) => {
            const results = [];
            for (let idx = 0; idx < 20; idx++) {
                const b = this.cpu.read(pc);
                const op = this.cpu.getOpInfo(b);
                results.push(this.dumpPC(pc));
                pc += sizes[op.mode];
            }
            return results;
        };
    }
    stepCycles(cycles) {
        this.cpu.stepCyclesDebug(this.verbose ? 1 : cycles, () => {
            var _a;
            const info = this.cpu.getDebugInfo();
            if ((_a = this.breakpoints.get(info.pc)) === null || _a === void 0 ? void 0 : _a(info)) {
                debug('breakpoint', this.printDebugInfo(info));
                this.container.stop();
                return true;
            }
            if (this.verbose) {
                debug(this.printDebugInfo(info));
            }
            else {
                this.updateTrace(info);
            }
        });
    }
    /**
     * Reads a range of memory. Will wrap at memory limit.
     *
     * @param address Starting address to read memory
     * @param length Length of memory to read.
     * @returns Byte array containing memory
     */
    getMemory(address, length) {
        const bytes = new Uint8Array(length);
        for (let idx = 0; idx < length; idx++) {
            address &= 0xffff;
            bytes[idx] = this.cpu.read(address++);
        }
        return bytes;
    }
    /**
     * Writes a range of memory. Will wrap at memory limit.
     *
     * @param address Starting address to write memory
     * @param bytes Data to write
     */
    setMemory(address, bytes) {
        for (const byte of bytes) {
            address &= 0xffff;
            this.cpu.write(address++, byte);
        }
    }
    updateTrace(info) {
        this.trace.push(info);
        if (this.trace.length > this.maxTrace) {
            this.trace.shift();
        }
    }
    padWithSymbol(pc) {
        const padding = '          ';
        const symbol = this.symbols[pc];
        let result = padding;
        if (symbol) {
            result = `${symbol}${padding.substring(symbol.length)}`;
        }
        return result;
    }
    dumpRawOp(parts) {
        const result = new Array(4);
        for (let idx = 0; idx < 4; idx++) {
            if (idx < parts.length) {
                result[idx] = toHex(parts[idx]);
            }
            else {
                result[idx] = '  ';
            }
        }
        return result.join(' ');
    }
    dumpOp(pc, parts) {
        const op = this.cpu.getOpInfo(parts[0]);
        const lsb = parts[1];
        const msb = parts[2];
        const addr = (msb << 8) | lsb;
        let val;
        let off;
        const toHexOrSymbol = (v, n) => this.symbols[v] || '$' + toHex(v, n);
        let result = op.name + ' ';
        switch (op.mode) {
            case 'implied':
                break;
            case 'immediate':
                result += `#${toHexOrSymbol(lsb)}`;
                break;
            case 'absolute':
                result += `${toHexOrSymbol(addr, 4)}`;
                break;
            case 'zeroPage':
                result += `${toHexOrSymbol(lsb)}`;
                break;
            case 'relative':
                {
                    off = lsb;
                    if (off > 127) {
                        off -= 256;
                    }
                    pc += off + 2;
                    result += `${toHexOrSymbol(pc, 4)} (${off})`;
                }
                break;
            case 'absoluteX':
                result += `${toHexOrSymbol(addr, 4)},X`;
                break;
            case 'absoluteY':
                result += `${toHexOrSymbol(addr, 4)},Y`;
                break;
            case 'zeroPageX':
                result += `${toHexOrSymbol(lsb)},X`;
                break;
            case 'zeroPageY':
                result += `${toHexOrSymbol(lsb)},Y`;
                break;
            case 'absoluteIndirect':
                result += `(${toHexOrSymbol(addr, 4)})`;
                break;
            case 'zeroPageXIndirect':
                result += `(${toHexOrSymbol(lsb)},X)`;
                break;
            case 'zeroPageIndirectY':
                result += `(${toHexOrSymbol(lsb)},),Y`;
                break;
            case 'accumulator':
                result += 'A';
                break;
            case 'zeroPageIndirect':
                result += `(${toHexOrSymbol(lsb)})`;
                break;
            case 'absoluteXIndirect':
                result += `(${toHexOrSymbol(addr, 4)},X)`;
                break;
            case 'zeroPage_relative':
                val = lsb;
                off = msb;
                if (off > 127) {
                    off -= 256;
                }
                pc += off + 2;
                result += `${toHexOrSymbol(val)},${toHexOrSymbol(pc, 4)} (${off})`;
                break;
            default:
                break;
        }
        return result;
    }
}
//# sourceMappingURL=debugger.js.map